import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { MonoText, ProviderChip, StatusPill, IconButton, RowActions } from '../../components/ui/YoolTable';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useToast } from '../../hooks/use-toast';
import { Loader2, Download, CreditCard, AlertCircle, CheckCircle, RefreshCw, DollarSign, XCircle } from 'lucide-react';
import PaymentsService from '../../services/paymentsService';
import PaymentsDataTable from '../../components/payments/PaymentsDataTable';
import { KpiGrid } from '../../components/ui/KpiCard';
import PaymentDetailModal from '../../components/payments/PaymentDetailModal';
import { instanteDeDb, msDeDb, fmtFechaDb } from '../../lib/fechas';

const PaymentsList = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    paid: 0,
    failed: 0
  });
  const { toast } = useToast();

  useEffect(() => {
    loadPayments();
  }, []);

  const loadPayments = async () => {
    try {
      setLoading(true);
      const data = await PaymentsService.getPayments();
      
      if (data && Array.isArray(data)) {
        // Mapear los datos de Supabase al formato esperado por la tabla
        const mappedPayments = data.map(payment => {
          // Obtener nombre del titular desde contratacion
          const getTitularName = () => {
            if (payment.contratacion?.datos_titular) {
              const titular = payment.contratacion.datos_titular;
              if (typeof titular === 'string') {
                try {
                  const parsed = JSON.parse(titular);
                  return parsed.nombre || parsed.name || 'Cliente no disponible';
                } catch {
                  return titular;
                }
              }
              return titular.nombre || titular.name || 'Cliente no disponible';
            }
            return 'Cliente no disponible';
          };

          const rawDate =
            payment.transaction_date ||
            payment.created_at ||
            payment.orden_pago?.created_at ||
            payment.contratacion?.created_at ||
            null;

          return {
            id: payment.id,
            reference: payment.session_id || `PAY-${payment.id}`,
            clientName: getTitularName(),
            amount: payment.amount,
            paymentMethod: payment.payment_type_code || null,
            status: payment.status,
            date: instanteDeDb(rawDate),
            contratacion_id: payment.contratacion_id,
            orden_pago_id: payment.orden_pago_id,
            // Datos adicionales para el detalle
            rawData: payment
          };
        });

        const sortedPayments = [...mappedPayments].sort((a, b) => {
          const aTime = msDeDb(a?.date);
          const bTime = msDeDb(b?.date);
          if (aTime === null && bTime === null) return 0;
          if (aTime === null) return 1;
          if (bTime === null) return -1;
          return bTime - aTime;
        });
        
        setPayments(sortedPayments);
        
        // Calcular estadísticas
        const totalAmount = sortedPayments.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
        const completedPayments = sortedPayments.filter(p => p.status === 'completed').length;
        const paidPayments = sortedPayments.filter(p => p.status === 'paid').length;
        const pendingPayments = sortedPayments.filter(p => p.status === 'pending').length;
        const failedPayments = sortedPayments.filter(p => p.status === 'failed').length;
        
        setStats({
          total: sortedPayments.length,
          pending: pendingPayments,
          completed: completedPayments,
          paid: paidPayments,
          failed: failedPayments
        });
      } else {
        toast({
          title: "Error",
          description: "No se pudieron cargar los pagos",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error loading payments:', error);
      toast({
        title: "Error",
        description: "Error al cargar los pagos",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      accessorKey: 'reference',
      header: 'Referencia',
      cell: ({ row }) => <MonoText>{row.getValue('reference')}</MonoText>,
    },
    {
      accessorKey: 'clientName',
      header: 'Cliente',
      cell: ({ row }) => (
        <span style={{ fontWeight: 600, color: '#1A1A1A' }}>{row.getValue('clientName')}</span>
      ),
    },
    {
      accessorKey: 'amount',
      header: 'Monto',
      cell: ({ row }) => {
        const amount = parseFloat(row.getValue('amount'));
        const formatted = new Intl.NumberFormat('es-CL', {
          style: 'currency',
          currency: 'CLP',
        }).format(amount);
        return <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatted}</span>;
      },
    },
    {
      accessorKey: 'status',
      header: 'Estado',
      cell: ({ row }) => <StatusPill estado={row.getValue('status')} tone="ring" />,
    },
    {
      accessorKey: 'paymentMethod',
      header: 'Método',
      cell: ({ row }) => <ProviderChip id={row.getValue('paymentMethod')} />,
    },
    {
       accessorKey: 'date',
       header: 'Fecha',
       cell: ({ row }) => (
         // `date` ya viene normalizado a instante (instanteDeDb, arriba);
         // `fmtFechaDb` lo escribe en hora de Chile. Sin la zona explícita el día
         // se corre para los pagos de la noche.
         <span style={{ color: '#6B7176', fontSize: 13 }}>{fmtFechaDb(row.getValue('date'))}</span>
       ),
     },
    {
      id: 'actions',
      header: 'Acciones',
      cell: ({ row }) => (
        <RowActions>
          <IconButton icon="eye" title="Ver detalle" onClick={(e) => { e.stopPropagation(); handleViewDetail(row.original); }} />
        </RowActions>
      ),
    },
  ];

  // Función para manejar la visualización del detalle
  const handleViewDetail = (payment) => {
    setSelectedPayment(payment);
    setShowDetailModal(true);
  };

  // Función para cerrar el modal
  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedPayment(null);
  };

  const filteredPayments = useMemo(() => {
    return payments.filter(payment => {
      const matchesSearch = payment.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           payment.reference.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'todos' || payment.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [payments, searchTerm, statusFilter]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Gestión de Pagos</h1>
        <div className="flex gap-2">
          <Button onClick={loadPayments} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
          <Button>
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </Button>
        </div>
      </div>

      {/* Estadísticas de Pagos */}
      {stats && (
        <KpiGrid
          cards={[
            {
              title: "Total Pagos",
              value: stats.total,
              description: "Pagos registrados",
              icon: CreditCard,
              trend: "neutral",
              color: "blue"
            },
            {
              title: "Pagos Completados",
              value: stats.completed,
              description: "Transacciones exitosas",
              icon: CheckCircle,
              trend: "up",
              color: "green"
            },
            {
              title: "Pagos Pendientes",
              value: stats.pending,
              description: "En proceso",
              icon: AlertCircle,
              trend: "neutral",
              color: "yellow"
            },
            {
              title: "Pagos Fallidos",
              value: stats.failed,
              description: "Transacciones fallidas",
              icon: XCircle,
              trend: stats.failed > 0 ? "down" : "neutral",
              color: stats.failed > 0 ? "red" : "gray"
            }
          ]}
        />
      )}



      <Card>
         <CardHeader className="flex flex-row items-center justify-between">
           <CardTitle>Lista de Pagos</CardTitle>
           <Button variant="outline" onClick={loadPayments} disabled={loading}>
             <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
             Actualizar
           </Button>
         </CardHeader>
        <CardContent>
          <div className="mb-4">
            <Tabs value={statusFilter} onValueChange={setStatusFilter}>
              <TabsList className="w-full justify-start">
                <TabsTrigger value="todos">Todos</TabsTrigger>
                <TabsTrigger value="pending">Pendientes</TabsTrigger>
                <TabsTrigger value="processing">En proceso</TabsTrigger>
                <TabsTrigger value="completed">Completados</TabsTrigger>
                <TabsTrigger value="paid">Pagados</TabsTrigger>
                <TabsTrigger value="failed">Fallidos</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          {loading ? (
              <div className="text-center py-8">Cargando pagos...</div>
            ) : (
             <PaymentsDataTable 
                data={filteredPayments}
                columns={columns}
                onRowClick={handleViewDetail}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
              />
            )}
        </CardContent>
      </Card>

      {/* Modal de detalle del pago */}
      {selectedPayment && (
        <PaymentDetailModal
          payment={selectedPayment?.rawData || selectedPayment}
          isOpen={showDetailModal}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default PaymentsList;
